/**
 * Share Hooks
 *
 * React Query hooks for managing reflection share links.
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { listShares, createShare, revokeShare } from "@/services/shares";
import type { ShareCreateRequest } from "@/types/share";

/**
 * Fetch all shares for a reflection
 */
export function useShares(reflectionId: string | undefined) {
  return useQuery({
    queryKey: ["shares", reflectionId],
    queryFn: () => listShares(reflectionId!),
    enabled: !!reflectionId,
  });
}

/**
 * Create a new share link
 */
export function useCreateShare() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (request: ShareCreateRequest) => createShare(request),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: ["shares", variables.reflection_id],
      });
    },
  });
}

/**
 * Revoke a share link
 */
export function useRevokeShare(reflectionId: string | undefined) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (shareId: string) => revokeShare(shareId),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["shares", reflectionId],
      });
    },
  });
}
